import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from 'react-query'

import { yupResolver } from '@hookform/resolvers/yup'
import { toast } from 'sonner'
import * as yup from 'yup'

import { LogoIcon } from '@/assets/icons'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useAppDispatch } from '@/hooks'
import authService from '@/services/auth-service'
import { login } from '@/slices/auth'
import { Loader2 } from 'lucide-react'
import { Link } from 'react-router-dom'

export interface LoginForm {
	email: string
	password: string
}

interface OtpForm {
	otp: string
}

export const Login = () => {
	const dispatch = useAppDispatch()

	const [isPasswordVisible, setIsPasswordVisible] = useState(false)
	const [requiresOtp, setRequiresOtp] = useState(false)
	const [pendingCredentials, setPendingCredentials] = useState<LoginForm | null>(null)

	const schema = yup.object<LoginForm>().shape({
		email: yup
			.string()
			.required('E-Mail ist erforderlich!')
			.email('Geben Sie eine gültige E-Mail-Adresse ein'),
		password: yup.string().required('Passwort ist erforderlich!'),
	})

	const otpSchema = yup.object<OtpForm>().shape({
		otp: yup
			.string()
			.required('Bestätigungscode ist erforderlich!')
			.matches(/^\d{6}$/, 'Der Code muss aus 6 Ziffern bestehen'),
	})

	const form = useForm<LoginForm>({
		resolver: yupResolver(schema),
		defaultValues: { email: '', password: '' },
		mode: 'all',
	})

	const otpForm = useForm<OtpForm>({
		resolver: yupResolver(otpSchema),
		defaultValues: { otp: '' },
		mode: 'all',
	})

	useEffect(() => {
		if (!requiresOtp) return
		otpForm.reset({ otp: '' })
		const timer = window.setTimeout(() => {
			const el = document.getElementById('admin-otp-code') as HTMLInputElement | null
			if (el && (el.value.includes('@') || el.value.length > 6)) {
				el.value = ''
				otpForm.setValue('otp', '')
			}
			el?.focus()
		}, 50)
		return () => window.clearTimeout(timer)
	}, [requiresOtp, pendingCredentials?.email, otpForm])

	const { mutate, isLoading } = useMutation({ mutationKey: 'login', mutationFn: authService.login })
	const { mutate: verifyOtp, isLoading: isVerifyingOtp } = useMutation({
		mutationKey: 'verify-admin-otp',
		mutationFn: authService.verifyAdminOtp,
	})

	const onSubmit = (data: LoginForm) => {
		mutate(data, {
			onSuccess: response => {
				if (response?.data?.requiresOtp) {
					setPendingCredentials(data)
					setRequiresOtp(true)
					otpForm.reset({ otp: '' })
					toast.success(response?.message || 'Bestätigungscode wurde per E-Mail gesendet.')
					return
				}

				dispatch(login(response.data))
				toast.success('Login successfull')
			},
			onError: (error: any) => {
				toast.error(error?.response?.data?.message ?? error?.request ?? error?.message)
			},
		})
	}

	const onOtpSubmit = (data: OtpForm) => {
		if (!pendingCredentials?.email) {
			toast.error('Bitte melden Sie sich erneut an.')
			setRequiresOtp(false)
			return
		}

		verifyOtp(
			{ email: pendingCredentials.email, otp: data.otp },
			{
				onSuccess: response => {
					dispatch(login(response.data))
					toast.success('Login successfull')
				},
				onError: (error: any) => {
					toast.error(error?.response?.data?.message ?? error?.request ?? error?.message)
				},
			}
		)
	}

	const onResendOtp = () => {
		if (!pendingCredentials) {
			toast.error('Bitte melden Sie sich erneut an.')
			setRequiresOtp(false)
			return
		}

		mutate(pendingCredentials, {
			onSuccess: response => {
				if (response?.data?.requiresOtp) {
					otpForm.reset({ otp: '' })
					toast.success(response?.message || 'Neuer Bestätigungscode wurde gesendet.')
					return
				}
				dispatch(login(response.data))
				toast.success('Login successfull')
			},
			onError: (error: any) => {
				toast.error(error?.response?.data?.message ?? error?.request ?? error?.message)
			},
		})
	}

	return (
		<div className="bg-[#F7F8F8] overflow-auto h-screen">
			<div className="lg:container max-lg:mx-5 pt-10 lg:pt-[77px] mx-auto flex flex-col items-center">
				<div className="flex items-center gap-[7px]">
					<Link to="/">
						<LogoIcon className="max-lg:size-14" />
					</Link>
					<div className="flex flex-col gap-2 lg:gap-3">
						<h2 className="text-secondary lg:text-xl font-extrabold">minmini</h2>
						<p className="max-lg:text-xs max-lg:w-2/3 text-muted-foreground">
							(Ver)kaufe nachhaltig, schaffe Platz und verdiene dabei.
						</p>
					</div>
				</div>
				<div className="bg-white w-11/12 lg:min-w-[529px] lg:w-[35%] mt-1.5 lg:mt-3 rounded-3xl lg:rounded-[32px] pt-5 lg:pt-10 pb-[18px] lg:pb-5 px-4 lg:px-10 flex flex-col">
					{requiresOtp ? (
						<>
							<h1 className="text-secondary font-bold lg:text-2xl">Bestätigungscode</h1>
							<p className="mt-2 max-lg:text-xs text-muted-foreground font-medium">
								Wir haben einen 6-stelligen Code an{' '}
								<span className="text-secondary font-semibold">
									{pendingCredentials?.email}
								</span>{' '}
								gesendet. Bitte gib ihn hier ein.
							</p>
							<Form {...otpForm}>
								<form
									key={`otp-form-${pendingCredentials?.email || 'pending'}`}
									autoComplete="off"
									onSubmit={otpForm.handleSubmit(onOtpSubmit)}
									className="flex flex-col mt-8 lg:mt-[38px]">
									{/* Soak up browser username/password autofill so it doesn't lock the OTP field */}
									<input
										type="text"
										name="username"
										autoComplete="username"
										tabIndex={-1}
										aria-hidden="true"
										className="sr-only absolute opacity-0 pointer-events-none h-0 w-0"
										defaultValue={pendingCredentials?.email || ''}
										readOnly
									/>
									<input
										type="password"
										name="password"
										autoComplete="current-password"
										tabIndex={-1}
										aria-hidden="true"
										className="sr-only absolute opacity-0 pointer-events-none h-0 w-0"
										defaultValue=""
										readOnly
									/>
									<FormField
										control={otpForm.control}
										name="otp"
										render={({ field }) => (
											<FormItem>
												<FormLabel>Code</FormLabel>
												<FormControl>
													<Input
														name="admin-otp-code"
														id="admin-otp-code"
														type="text"
														inputMode="numeric"
														pattern="[0-9]*"
														autoComplete="one-time-code"
														autoCorrect="off"
														autoCapitalize="off"
														spellCheck={false}
														maxLength={6}
														placeholder="123456"
														value={field.value}
														onBlur={field.onBlur}
														ref={field.ref}
														onChange={e => {
															const digits = e.target.value.replace(/\D/g, '').slice(0, 6)
															field.onChange(digits)
														}}
														onFocus={e => {
															// Clear Chrome autofilled email if it snuck in
															if (e.target.value.includes('@')) {
																field.onChange('')
															}
														}}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<Button
										type="submit"
										className="rounded-lg mt-[38px] lg:mt-16 lg:text-xl font-medium"
										disabled={isVerifyingOtp || isLoading}
										variant="secondary">
										{(isVerifyingOtp || isLoading) && <Loader2 className="animate-spin" />}
										{isVerifyingOtp || isLoading ? 'Bitte warten' : 'Bestätigen'}
									</Button>
									<button
										type="button"
										onClick={onResendOtp}
										disabled={isLoading || isVerifyingOtp}
										className="mt-4 text-center text-muted-foreground text-xs font-medium cursor-pointer disabled:opacity-50">
										Code erneut senden
									</button>
									<button
										type="button"
										onClick={() => {
											setRequiresOtp(false)
											setPendingCredentials(null)
											otpForm.reset({ otp: '' })
										}}
										className="mt-2 text-center text-secondary text-xs font-semibold cursor-pointer">
										Zurück zur Anmeldung
									</button>
								</form>
							</Form>
						</>
					) : (
						<>
							<h1 className="text-secondary font-bold lg:text-2xl">Anmelden</h1>
							<p className="mt-2 max-lg:text-xs text-muted-foreground font-medium">
								Schön, dich wiederzusehen!
							</p>
							<Form {...form}>
								<form
									onSubmit={form.handleSubmit(onSubmit)}
									className="flex flex-col mt-8 lg:mt-[38px]">
									<FormField
										control={form.control}
										name="email"
										render={({ field }) => (
											<FormItem>
												<FormLabel>E-Mail</FormLabel>
												<FormControl>
													<Input {...field} />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="password"
										render={({ field }) => (
											<FormItem className="mt-8">
												<FormLabel>Password</FormLabel>
												<FormControl>
													<div className="relative">
														<Input type={isPasswordVisible ? 'text' : 'password'} {...field} />
														{isPasswordVisible ? (
															<EyeIcon
																onClick={() => setIsPasswordVisible(false)}
																className="size-5 lg:size-6 absolute right-4 top-1/2 -translate-y-1/2 text-secondary cursor-pointer"
															/>
														) : (
															<EyeSlashIcon
																onClick={() => setIsPasswordVisible(true)}
																className="size-5 lg:size-6 absolute right-4 top-1/2 -translate-y-1/2 text-secondary cursor-pointer"
															/>
														)}
													</div>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<Link
										to="/forgot-password"
										className="text-right w-fit ml-auto mt-3 text-muted-foreground text-xs font-medium cursor-pointer">
										Passwort Vergessen?
									</Link>
									<div className="flex items-center mt-5 lg:mt-1 space-x-2">
										<Checkbox id="remember-me" />
										<label
											htmlFor="remember-me"
											className="text-xs text-muted-foreground font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
											Angemeldet bleiben
										</label>
									</div>
									<Button
										className="rounded-lg mt-[38px] lg:mt-16 lg:text-xl font-medium"
										disabled={isLoading}
										variant="secondary">
										{isLoading && <Loader2 className="animate-spin" />}
										{isLoading ? 'Bitte warten' : 'Anmelden'}
									</Button>
									<p className="mt-3 lg:mt-5 text-center text-muted-foreground text-[10px] lg:text-sm font-medium">
										Du hast noch keinen Zugang?{' '}
										<span className="text-secondary font-extrabold">Buche jetzt dein Regal</span>
									</p>
								</form>
							</Form>
						</>
					)}
				</div>
			</div>
		</div>
	)
}
